// ─── Permission Definitions ───────────────────────────────────────────────────
export type Permission =
  | 'dashboard:view'
  | 'events:view' | 'events:write' | 'events:delete'
  | 'registrations:view' | 'registrations:approve' | 'registrations:delete'
  | 'applications:view' | 'applications:approve' | 'applications:delete'
  | 'guests:view' | 'guests:write' | 'guests:delete'
  | 'donors:view' | 'donors:write' | 'donors:delete'
  | 'media:view' | 'media:write' | 'media:delete'
  | 'staff:view' | 'staff:write' | 'staff:delete'
  | 'email:view' | 'email:write' | 'email:delete'
  | 'subscriptions:view' | 'subscriptions:write' | 'subscriptions:delete'
  | 'settings:view';

// ─── Role Definitions ─────────────────────────────────────────────────────────
export type RoleKey = 'super_admin' | 'event_manager' | 'content_manager' | 'reviewer' | 'viewer';

export interface RoleDef {
  key: RoleKey;
  label: string;
  labelEn: string;
  color: string;    // Tailwind bg + text classes
  description: string;
  permissions: Permission[];
}

const ALL_PERMISSIONS: Permission[] = [
  'dashboard:view',
  'events:view', 'events:write', 'events:delete',
  'registrations:view', 'registrations:approve', 'registrations:delete',
  'applications:view', 'applications:approve', 'applications:delete',
  'guests:view', 'guests:write', 'guests:delete',
  'donors:view', 'donors:write', 'donors:delete',
  'media:view', 'media:write', 'media:delete',
  'staff:view', 'staff:write', 'staff:delete',
  'email:view', 'email:write', 'email:delete',
  'subscriptions:view', 'subscriptions:write', 'subscriptions:delete',
  'settings:view',
];

export const ROLES: Record<RoleKey, RoleDef> = {
  super_admin: {
    key: 'super_admin',
    label: '超级管理员',
    labelEn: 'Super Admin',
    color: 'bg-red-100 text-red-700',
    description: '拥有所有权限，可管理工作人员和角色',
    permissions: ALL_PERMISSIONS,
  },
  event_manager: {
    key: 'event_manager',
    label: '活动运营',
    labelEn: 'Event Manager',
    color: 'bg-amber-100 text-amber-700',
    description: '管理活动、报名及合作申请的全流程',
    permissions: [
      'dashboard:view',
      'events:view', 'events:write', 'events:delete',
      'registrations:view', 'registrations:approve', 'registrations:delete',
      'applications:view', 'applications:approve', 'applications:delete',
    ],
  },
  content_manager: {
    key: 'content_manager',
    label: '内容管理员',
    labelEn: 'Content Manager',
    color: 'bg-emerald-100 text-emerald-700',
    description: '管理嘉宾、捐赠人及媒体报道内容',
    permissions: [
      'dashboard:view',
      'guests:view', 'guests:write', 'guests:delete',
      'donors:view', 'donors:write', 'donors:delete',
      'media:view', 'media:write', 'media:delete',
    ],
  },
  reviewer: {
    key: 'reviewer',
    label: '审核员',
    labelEn: 'Reviewer',
    color: 'bg-sky-100 text-sky-700',
    description: '可查看数据并审批报名和申请，不可修改内容',
    permissions: [
      'dashboard:view',
      'events:view',
      'registrations:view', 'registrations:approve',
      'applications:view', 'applications:approve',
      'guests:view',
      'donors:view',
      'media:view',
      'staff:view',
    ],
  },
  viewer: {
    key: 'viewer',
    label: '只读查看',
    labelEn: 'Viewer',
    color: 'bg-gray-100 text-gray-600',
    description: '仅可查看所有数据，无任何修改权限',
    permissions: [
      'dashboard:view',
      'events:view',
      'registrations:view',
      'applications:view',
      'guests:view',
      'donors:view',
      'media:view',
    ],
  },
};

export const ROLE_ORDER: RoleKey[] = ['super_admin', 'event_manager', 'content_manager', 'reviewer', 'viewer'];

// ─── Permission Groups for Display ───────────────────────────────────────────
export interface PermissionGroup {
  label: string;
  icon: string;
  permissions: { key: Permission; label: string }[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    label: '数据总览', icon: 'ri-dashboard-3-line',
    permissions: [{ key: 'dashboard:view', label: '查看 Dashboard' }],
  },
  {
    label: '活动管理', icon: 'ri-calendar-event-line',
    permissions: [
      { key: 'events:view', label: '查看活动' },
      { key: 'events:write', label: '创建/编辑活动' },
      { key: 'events:delete', label: '删除活动' },
    ],
  },
  {
    label: '活动报名', icon: 'ri-calendar-check-line',
    permissions: [
      { key: 'registrations:view', label: '查看报名' },
      { key: 'registrations:approve', label: '审批报名' },
      { key: 'registrations:delete', label: '删除报名' },
    ],
  },
  {
    label: '合作申请', icon: 'ri-file-list-3-line',
    permissions: [
      { key: 'applications:view', label: '查看申请' },
      { key: 'applications:approve', label: '审批申请' },
      { key: 'applications:delete', label: '删除申请' },
    ],
  },
  {
    label: '嘉宾管理', icon: 'ri-user-star-line',
    permissions: [
      { key: 'guests:view', label: '查看嘉宾' },
      { key: 'guests:write', label: '编辑嘉宾' },
      { key: 'guests:delete', label: '删除嘉宾' },
    ],
  },
  {
    label: '捐赠人', icon: 'ri-heart-line',
    permissions: [
      { key: 'donors:view', label: '查看捐赠人' },
      { key: 'donors:write', label: '编辑捐赠人' },
      { key: 'donors:delete', label: '删除捐赠人' },
    ],
  },
  {
    label: '媒体报道', icon: 'ri-newspaper-line',
    permissions: [
      { key: 'media:view', label: '查看媒体' },
      { key: 'media:write', label: '编辑媒体' },
      { key: 'media:delete', label: '删除媒体' },
    ],
  },
  {
    label: '邮件配置', icon: 'ri-mail-settings-line',
    permissions: [
      { key: 'email:view', label: '查看邮件配置' },
      { key: 'email:write', label: '编辑邮件配置' },
      { key: 'email:delete', label: '删除邮件配置' },
    ],
  },
  {
    label: '工作人员', icon: 'ri-team-line',
    permissions: [
      { key: 'staff:view', label: '查看人员' },
      { key: 'staff:write', label: '管理人员' },
      { key: 'staff:delete', label: '删除人员' },
    ],
  },
  {
    label: '订阅管理', icon: 'ri-mail-check-line',
    permissions: [
      { key: 'subscriptions:view', label: '查看订阅' },
      { key: 'subscriptions:write', label: '发送邮件' },
      { key: 'subscriptions:delete', label: '删除订阅' },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Check if a single role has a permission */
export function hasPermission(roleKey: RoleKey | undefined, permission: Permission): boolean {
  if (!roleKey) return false;
  return ROLES[roleKey]?.permissions.includes(permission) ?? false;
}

/** Check if ANY of multiple roles grants a permission (union of permissions) */
export function hasPermissionMulti(roles: RoleKey[], permission: Permission): boolean {
  return roles.some((rk) => ROLES[rk]?.permissions.includes(permission));
}

/** Get union of all permissions across multiple roles */
export function mergePermissions(roles: RoleKey[]): Permission[] {
  const set = new Set<Permission>();
  roles.forEach((rk) => ROLES[rk]?.permissions.forEach((p) => set.add(p)));
  return Array.from(set);
}

export function getRoleLabel(roleKey: RoleKey): string {
  return ROLES[roleKey]?.label ?? roleKey;
}
